<template>
  <omni-canvas
    name="studio"
    class="studio-canvas"
    :class="{fill}"
    :width="cnvWidth"
    :height="cnvHeight"
    :on-element-selected="onElementSelected"
    :on-element-deselected="onElementDeselected"
    :on-element-activated="onElementActivated"
    :on-element-added="onElementAdded"
    :on-element-deleted="onElementDeleted"
    :layers="canvasLayers"
    :min-zoom="minZoom"
    :max-zoom="maxZoom"
    :show-dimensions="showDimensions"
    :background-fill="backgroundFill"
    :custom-options="customOptions"
    :with-classes="omniClasses"
    @initialised="onCanvasInitialised"
    @destroyed="onCanvasDestroyed"
    @drop.prevent="onFileDrop($event)"
    @dragover.prevent
    @canvas-click="$emit('canvas-click', $event)"
  />
</template>

<script>
import {Vector} from '@teemill/common/classes';

import {OmniImage, OmniText, OmniPolygon} from '../../../omnicanvas/objects';
import OmniCanvas from '../../../omnicanvas/components/OmniCanvas.vue';
import clamp from 'lodash/clamp';

import {isDeviceIos, pdfToImage} from '@teemill/common/helpers';

export default {
  name: 'StudioCanvas',

  components: {
    OmniCanvas,
  },
  inject: [
    'logger',
    'isMobile',
    'getVuexProperty',
    'setVuexProperty',
    'getCanvas',
    'addResizeListener',
    'getNavHeight',
    'onElementSelected',
    'onElementDeselected',
    'onElementActivated',
    'onElementAdded',
    'onElementDeleted',
    'createImageElement',
    'minZoom',
    'maxZoom',
  ],

  props: {
    /**
     * @prop {Number} minWidth
     * The minimum width of the HTMLCanvasElement
     */
    minWidth: {
      type: Number,
      default: 1200,
    },

    /**
     * @prop {Number} minHeight
     * The minimum height of the HTMLCanvasElement
     */
    minHeight: {
      type: Number,
      default: 1200,
    },

    /**
     * @prop {Number} width
     * The target width of the HTMLCanvasElement
     */
    width: {
      type: Number,
      default: 1200,
    },

    /**
     * @prop {Number} height
     * The target height of the HTMLCanvasElement
     */
    height: {
      type: Number,
      default: 1200,
    },

    /**
     * @prop {Number} maxWidth
     * The maximum width of the HTMLCanvasElement
     */
    maxWidth: {
      type: Number,
      default: 1200,
    },

    /**
     * @prop {Number} maxHeight
     * The maximum height of the HTMLCanvasElement
     */
    maxHeight: {
      type: Number,
      default: 2400,
    },

    fill: Boolean,

    showDimensions: Boolean,

    backgroundFill: {
      type: String,
    },

    customOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      /**
       * @param {Number} width the width of the canvas
       * @param {Number} height the height of the canvas
       * @param {FileReader} reader a file reader used for uploading images
       */
      cnvWidth: this.width ? this.width : this.minWidth,
      cnvHeight: this.height ? this.height : this.minHeight,
      reader: false,

      canvasLayers: [
        {
          tag: 'front',
          img: new Image(),
          imgCoords: {
            position: new Vector(835, 718),
            width: 917,
            height: 1221,
          },
          sortorder: 1,
        },
        {
          tag: 'back',
          img: new Image(),
          imgCoords: {
            position: new Vector(835, 554),
            width: 917,
            height: 1221,
          },
          sortorder: 2,
        },
      ],

      omniClasses: {
        Image: OmniImage,
        Text: OmniText,
        Polygon: OmniPolygon,
      },
    };
  },
  mounted() {
    this.createFileReader();
  },
  methods: {
    normaliseSvgDataUrlDimensions(fileContents) {
      if (
        typeof fileContents !== 'string' ||
        !fileContents.startsWith('data:image/svg+xml')
      ) {
        return fileContents;
      }

      const separatorIndex = fileContents.indexOf(',');
      if (separatorIndex === -1) {
        return fileContents;
      }

      const header = fileContents.slice(0, separatorIndex);
      const payload = fileContents.slice(separatorIndex + 1);
      const isBase64 = /;base64/i.test(header);

      let svgContent;
      try {
        svgContent = isBase64 ? atob(payload) : decodeURIComponent(payload);
      } catch (error) {
        return fileContents;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(svgContent, 'image/svg+xml');
      const svgElement = doc.documentElement;

      if (!svgElement || svgElement.nodeName.toLowerCase() !== 'svg') {
        return fileContents;
      }

      if (
        svgElement.hasAttribute('width') &&
        svgElement.hasAttribute('height')
      ) {
        return fileContents;
      }

      const viewBox = svgElement.getAttribute('viewBox');
      if (!viewBox) {
        return fileContents;
      }

      const viewBoxParts = viewBox.trim().split(/[\s,]+/);
      if (viewBoxParts.length < 4) {
        return fileContents;
      }

      const inferredWidth = Number.parseFloat(viewBoxParts[2]);
      const inferredHeight = Number.parseFloat(viewBoxParts[3]);

      if (
        !Number.isFinite(inferredWidth) ||
        !Number.isFinite(inferredHeight) ||
        inferredWidth <= 0 ||
        inferredHeight <= 0
      ) {
        return fileContents;
      }

      if (!svgElement.hasAttribute('width')) {
        svgElement.setAttribute('width', `${inferredWidth}`);
      }
      if (!svgElement.hasAttribute('height')) {
        svgElement.setAttribute('height', `${inferredHeight}`);
      }

      const serialised = new XMLSerializer().serializeToString(doc);

      try {
        if (isBase64) {
          const encoded = btoa(unescape(encodeURIComponent(serialised)));
          return `${header},${encoded}`;
        }

        return `${header},${encodeURIComponent(serialised)}`;
      } catch (error) {
        return fileContents;
      }
    },

    /**
     * Resizes the canvas to fit the screen
     *
     * @return {void}
     */
    setStudioCanvasHeight() {
      const cnv = this.getCanvas().canvasElement;
      const cnvBounds = cnv.getBoundingClientRect();
      const studioArea = cnv.closest('.studio-body-container');

      const windowHeight =
        window.innerHeight -
        (cnvBounds.y || cnvBounds.top || 60) -
        (this.isMobile() ? this.getNavHeight() : 0);
      const windowWidth = studioArea?.offsetWidth ?? 1200;
      const windowAspectRatio = windowWidth / windowHeight;
      const targetAspectRatio = this.minWidth / this.minHeight;

      if (windowWidth && this.fill) {
        cnv.width = studioArea?.offsetWidth ?? 1200;
        cnv.height = studioArea?.offsetHeight ?? 1200;

        this.cnvWidth = cnv.width;
        this.cnvHeight = cnv.height;

        this.getCanvas().updateDimensions(cnv.width, cnv.height);
      } else {
        if (targetAspectRatio > windowAspectRatio) {
          cnv.width = this.minWidth;
          cnv.height = this.minHeight * (targetAspectRatio / windowAspectRatio);
        } else {
          cnv.width = this.minWidth * (windowAspectRatio / targetAspectRatio);
          cnv.height = this.minHeight;
        }

        cnv.width = clamp(cnv.width, this.minWidth, this.maxWidth);
        cnv.height = clamp(cnv.height, this.minHeight, this.maxHeight);
      }
      this.getCanvas().render();
    },

    /**
     * Event that is fired when a file is dropped using the HTML5 drag and drop
     * (in supported browsers)
     *
     * @param {Event} event The browser event
     * @return {void}
     */
    onFileDrop(event) {
      let file;
      if (event?.dataTransfer?.items?.length) {
        file = event.dataTransfer.items[0].getAsFile();
      } else {
        file = event.dataTransfer.files[0];
      }

      if (file) {
        if (
          !file.type.match(/^image\/.*$/) &&
          file.type !== 'application/pdf'
        ) {
          this.$dialogs.showSnackBar({
            message:
              'This file type is not supported. Please upload .png or .jpg files',
          });
          return;
        }

        if (file.type === 'application/pdf') {
          // Read the PDF as an ArrayBuffer and pass the raw bytes to pdfToImage
          const pdfReader = new FileReader();
          pdfReader.onload = ev => {
            const buffer = ev?.target?.result;
            if (!buffer) {
              this.$dialogs.showSnackBar({
                message:
                  'PDF file could not be read. Please try again with a different file.',
              });
              return;
            }

            pdfToImage(buffer)
              .then(img => {
                this.onFileUpload({target: {result: img[0]}});
              })
              .catch(() => {
                this.$dialogs.showSnackBar({
                  message:
                    'PDF file could not be processed. Please try again with a different file.',
                });
              });
          };
          pdfReader.readAsArrayBuffer(file);
        } else {
          this.reader.readAsDataURL(file);
        }
      }
    },

    /**
     * Event that is fired when an uploaded file is read by the FileReader
     *
     * @param {Event} event The FileReader event
     * @return {void}
     */
    onFileUpload(event) {
      const fileContents = this.normaliseSvgDataUrlDimensions(
        event.target.result
      );
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        if (img.width > 16384 || img.height > 16384) {
          this.$dialogs.showSnackBar({
            message:
              'This image is too large. Please upload files smaller than 16,384 x 16,384 pixels',
          });
          return;
        }

        const boundingBox = this.getCanvas().boundingBox;
        let targetScale = img.width / boundingBox.width;

        const maxWidth = isDeviceIos() ? 4096 : 8192;

        if (img.width * img.height > maxWidth ** 2) {
          const scaleRatio = maxWidth / Math.sqrt(img.width * img.height);
          const targetWidth = img.width * scaleRatio;
          targetScale = targetWidth / boundingBox.width;
        }

        this.createImageElement(img, {}, false, targetScale);
      };
      img.src = fileContents;
    },

    /**
     * Create a new FileReader instance
     *
     * @return {void}
     */
    createFileReader() {
      const reader = new FileReader();
      reader.onload = this.onFileUpload;
      this.reader = reader;
    },

    onCanvasInitialised(omniCanvas) {
      window.studioCanvas = omniCanvas;

      this.$emit('canvas-initialised', omniCanvas);
      this.addResizeListener(() => {
        this.setStudioCanvasHeight();
      });
      this.setStudioCanvasHeight();

      this.$emit('after-canvas-initialised', omniCanvas);
    },

    onCanvasDestroyed() {
      window.studioCanvas = null;

      this.$emit('canvas-destroyed');
    },
  },
};
</script>

<style lang="scss">
.studio-canvas {
  .omni-canvas {
    max-width: 100%;
    display: block;
    margin: auto;
    position: relative;
  }

  &.fill {
    .omni-canvas {
      max-width: 100vw;
      z-index: initial;
    }
  }
}

@media (max-width: 991px) {
  .studio-canvas {
    .omni-canvas {
      max-height: calc(100vh - 112px);
    }
  }
}
@media (min-width: 992px) {
  .studio-canvas {
    .omni-canvas {
      max-height: var(--visible-content-height);
      margin: auto;
    }
  }
}
</style>
